/**
 * Session attach — refresh-recovery via GET /api/sessions/:id/attach.
 *
 * Not routed through HttpAgent (it only does POST /runs). Manual SSE parsing
 * with lazy assistant placeholder creation. Best-effort — if nothing is
 * running, the SSE closes immediately (no-op).
 *
 * Preserves exact behavior from useChat.ts lines 384-432.
 */

import { useCallback } from "react";
import type { AGUIEvent } from "@strands/agent-transport";
import { apiFetch } from "@/shared/api";
import { useChatStoreApi } from "../stores/chat-store-context";
import type { ChatStore } from "../stores/chat-store";
import { routeEvent } from "../stores/chat-event-handlers";

function parseSSELines(text: string): AGUIEvent[] {
  const events: AGUIEvent[] = [];
  for (const line of text.split("\n")) {
    const trimmed = line.trim();
    if (trimmed.startsWith("data: ")) {
      try {
        events.push(JSON.parse(trimmed.slice(6)));
      } catch {
        /* skip malformed */
      }
    }
  }
  return events;
}


// Module-level singleton: at most ONE attach reader may be live app-wide.
// Two attach readers — or an attach reader running concurrently with a live
// POST /runs stream — both consume the same backend broker and both call
// routeEvent on the SAME store, so every TEXT delta gets `+=` applied twice
// and the body becomes "Х"+"Х"="ХХ" garbled. A per-hook ref couldn't enforce
// this: loadHistory and useAgentStream hold separate useSessionAttach
// instances, so their refs never see each other. Hoisting to module scope
// makes the abort cross-instance.
//
// A one-shot abort at startRun() entry is NOT enough: loadHistory opens its
// attach asynchronously (route navigation → setActiveSession → loadHistory),
// racing the user's send. If startRun aborts before loadHistory has even
// registered its controller, the abort hits nothing and the attach opens
// right after — two consumers again. So we keep a *guard*: while a run is
// live for a sid, attach for that sid is forbidden (and any already-open one
// is aborted). This is order-independent — covers attach-first and run-first.
let activeAttachAC: AbortController | null = null;
let suppressedRunSid: string | null = null;

/** Mark a run live for `sid`: forbid + abort any attach for it. Called
 *  synchronously at the top of startRun(), before any await. */
export function beginRunSuppression(sid: string): void {
  suppressedRunSid = sid;
  activeAttachAC?.abort();
  activeAttachAC = null;
}

/** Clear the suppression once the run is done so refresh-recovery attach
 *  can work again. Called from startRun()'s finally. */
export function endRunSuppression(sid: string): void {
  if (suppressedRunSid === sid) suppressedRunSid = null;
}

export function useSessionAttach() {
  const storeApi = useChatStoreApi();

  const attachToSession = useCallback(async (sid: string) => {
    if (suppressedRunSid === sid) return;
    activeAttachAC?.abort();
    const ac = new AbortController();
    activeAttachAC = ac;
    let pushedPlaceholder = false;

    const ensurePlaceholder = () => {
      if (pushedPlaceholder) return;
      pushedPlaceholder = true;
      storeApi.setState((s) => {
        // Mid-session reattach: the last assistant is the in-progress
        // bubble from the dropped stream. Drop it — the backend's replay
        // (text deltas + tool events from the start of the message) will
        // rebuild it from scratch into the fresh placeholder we push
        // below. Without this we'd render two stacked assistant bubbles
        // for the same logical turn — the orphaned half-baked one and
        // the new one filled by the replay.
        // F5/mount path: loadHistory just set streaming=false and only
        // pushed completed messages, so this branch is a no-op.
        if (s.streaming) {
          const last = s.messages[s.messages.length - 1];
          if (last?.role === "assistant") {
            s.messages.pop();
            delete s.partsMap[last.id];
          }
        }
        const id = crypto.randomUUID();
        s.messages.push({ id, role: "assistant", sessionId: sid });
        s.partsMap[id] = [];
        s.streaming = true;
      });
    };

    const set = (fn: (draft: ChatStore) => void) => storeApi.setState(fn);
    const get = () => storeApi.getState();

    try {
      const resp = await apiFetch(`/api/sessions/${sid}/attach`, { signal: ac.signal });
      const reader = resp.body?.getReader();
      if (!reader) return;
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (ac.signal.aborted || get().activeSessionId !== sid) {
          await reader.cancel().catch(() => {});
          return;
        }
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() ?? "";
        for (const block of lines) {
          for (const event of parseSSELines(block)) {
            if (ac.signal.aborted) return;
            ensurePlaceholder();
            routeEvent(event, set, get, sid);
          }
        }
      }
      // Flush remaining buffer
      if (!ac.signal.aborted && buffer.trim() && get().activeSessionId === sid) {
        for (const event of parseSSELines(buffer)) {
          ensurePlaceholder();
          routeEvent(event, set, get, sid);
        }
      }
    } catch {
      /* ignore — attach is best-effort (also AbortError on supersede) */
    } finally {
      // Only the WINNING (non-aborted) attach should clear streaming — a
      // superseded attach must not flip the flag out from under its successor.
      if (activeAttachAC === ac) activeAttachAC = null;
      if (!ac.signal.aborted && get().activeSessionId === sid) {
        storeApi.setState((s) => {
          s.streaming = false;
        });
      }
    }
  }, [storeApi]);

  return { attachToSession };
}
